/**
 * SabotageConfigFields — the editable body of a {@link TrlSabotageConfig}.
 *
 * Game-level only. One toggle per sabotage type, rendered from the shared
 * registry so a new type appears here without UI changes; per-type tuning (the
 * Treasure Thief steal %) sits under its toggle. Purely controlled.
 */

import {
  TRL_SABOTAGE_DEFINITIONS,
  TRL_SABOTAGE_TYPES,
  TRL_THEFT_MAX_PERCENT,
  TRL_THEFT_MIN_PERCENT,
  type TrlSabotageConfig,
  type TrlSabotageType,
} from '@swtorbit/shared-socket-events';
import { getTrlSabotageIconUrl } from '@swtorbit/shared-assets';

interface SabotageConfigFieldsProps {
  value: TrlSabotageConfig;
  onChange: (next: TrlSabotageConfig) => void;
  disabled?: boolean;
}

export function SabotageConfigFields({ value, onChange, disabled = false }: SabotageConfigFieldsProps) {
  const setEnabled = (type: TrlSabotageType, enabled: boolean) =>
    onChange({ types: { ...value.types, [type]: { ...value.types[type], enabled } } });

  return (
    <div className="d-flex flex-column gap-sm">
      {TRL_SABOTAGE_TYPES.map((type) => {
        const def = TRL_SABOTAGE_DEFINITIONS[type];
        const enabled = value.types[type].enabled;
        return (
          <div key={type} className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
            <label className="d-flex align-items-center gap-2xs fs-sm">
              <input
                className="checkbox"
                type="checkbox"
                checked={enabled}
                disabled={disabled}
                onChange={(e) => setEnabled(type, e.target.checked)}
              />
              <img
                className="trl-sabotage-glyph trl-sabotage-glyph--lg"
                src={getTrlSabotageIconUrl(type)}
                alt=""
                aria-hidden="true"
              />
              <span>
                <strong>{def.label}</strong>
                <span className="fs-xs text-slate d-block">{def.description}</span>
              </span>
            </label>

            {type === 'treasure_thief' && enabled && (
              <label className="d-flex align-items-center gap-xs fs-sm">
                <span>Steal</span>
                <input
                  type="number"
                  className="input input--sm"
                  style={{ width: '5rem' }}
                  min={TRL_THEFT_MIN_PERCENT}
                  max={TRL_THEFT_MAX_PERCENT}
                  step={5}
                  disabled={disabled}
                  value={value.types.treasure_thief.stealPercent}
                  onChange={(e) =>
                    onChange({
                      types: {
                        ...value.types,
                        treasure_thief: { ...value.types.treasure_thief, stealPercent: Number(e.target.value) },
                      },
                    })
                  }
                />
                <span className="fs-xs text-slate">
                  % of the victim&apos;s treasure items per theft (at least 1). Lower-tier items are the
                  likeliest to go.
                </span>
              </label>
            )}

            {def.requiresDecoyImage && enabled && (
              <span className="fs-xs text-amber">
                Every planet except the starting planets and the final planet needs a fake image — set
                it in each planet&apos;s editor.
              </span>
            )}

            {def.requiresTriangulationNode && enabled && (
              <span className="fs-xs text-amber">
                Captains can only place this on a Triangulation planet, so the map needs at least one
                that is neither a starting planet nor the final planet.
              </span>
            )}
          </div>
        );
      })}

      <div className="border-md border-aqua-dark rounded-base p-xs d-flex flex-column gap-xs">
        <span className="fs-xs text-uppercase text-slate">Rules</span>
        <span className="fs-xs text-slate">
          In the lobby, each team&apos;s captain may secretly place one sabotage on one planet (never a
          starting planet or the final planet). It lasts the whole game and affects every other team
          that reaches that planet. Rival teams never see where a sabotage is or who placed it.
        </span>
        <span className="fs-xs text-slate">
          Sabotage settings lock, along with the map, once the game leaves setup.
        </span>
      </div>
    </div>
  );
}
